import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Presentation, FileQuestion, NotebookPen, Check, CheckCircle2, ScanLine } from "lucide-react";

const features = [
  { icon: Presentation, title: "AI Презентации", desc: "Готови слайдове по всяка тема за секунди." },
  { icon: FileQuestion, title: "Работни листове", desc: "Тестове, викторини и упражнения." },
  { icon: NotebookPen, title: "Резюме на урока", desc: "По българската учебна програма." },
  { icon: ScanLine, title: "Проверка на тестове", desc: "Снимай тест и AI го оценява." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user) navigate("/app");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setSent(true);
        toast.success(data.message || "Изпратихме инструкции, ако имейлът е регистриран.");
        return;
      }
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" ? form : { email: form.email, password: form.password, remember };
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success("Добре дошли!");
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Грешка");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left */}
      <div className="p-10 lg:p-16 flex flex-col justify-center">
        <div className="flex items-center gap-2 mb-8">
          <div className="bg-sky-500 text-white p-2 rounded-2xl"><Sparkles size={22} /></div>
          <span className="font-heading font-black text-2xl text-slate-900">УчителAI</span>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900 leading-tight"
        >
          Всичко за твоя <span className="text-sky-500">клас</span>,
          <br />създадено с <span className="text-amber-400">изкуствен интелект</span>.
        </motion.h1>
        <p className="mt-5 text-slate-500 text-lg max-w-md leading-relaxed">
          Презентации, работни листове, планове на уроци и печатни материали — на един клик разстояние.
        </p>
        <div className="grid grid-cols-2 gap-4 mt-10 max-w-md">
          {features.map((f, i) => (
            <motion.div
              key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * i }}
              className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm"
            >
              <div className="bg-sky-100 text-sky-600 w-9 h-9 rounded-full flex items-center justify-center mb-2"><f.icon size={18} /></div>
              <div className="font-heading font-bold text-slate-900 text-sm">{f.title}</div>
              <div className="text-xs text-slate-400 mt-0.5">{f.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Right - auth */}
      <div className="bg-white lg:border-l border-slate-100 flex items-center justify-center p-10">
        <div className="w-full max-w-sm">
          <div className="flex bg-slate-100 rounded-full p-1 mb-8">
            {["register", "login"].map((m) => (
              <button
                key={m} data-testid={`tab-${m}`} onClick={() => { setMode(m); setSent(false); }}
                className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-colors ${mode === m ? "bg-white text-sky-600 shadow-sm" : "text-slate-400"}`}
              >
                {m === "register" ? "Регистрация" : "Вход"}
              </button>
            ))}
          </div>

          {mode === "forgot" ? (
            sent ? (
              <div data-testid="forgot-sent" className="text-center py-4">
                <CheckCircle2 size={44} className="text-emerald-500 mx-auto mb-3" />
                <h2 className="font-heading text-xl font-bold text-slate-900">Проверете имейла си</h2>
                <p className="text-slate-500 mt-2 text-sm">Ако <b className="text-slate-700">{form.email}</b> е регистриран, изпратихме линк за смяна на паролата. Линкът е валиден 1 час.</p>
                <button data-testid="back-to-login-btn" onClick={() => { setMode("login"); setSent(false); }}
                  className="mt-6 w-full bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 rounded-full transition-colors">Обратно към входа</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <h2 className="font-heading text-xl font-bold text-slate-900">Забравена парола?</h2>
                  <p className="text-slate-500 mt-1 text-sm">Въведи имейла си и ще получиш линк за смяна на паролата.</p>
                </div>
                <input
                  data-testid="forgot-email-input" required type="email" placeholder="Имейл" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none transition-shadow"
                />
                <button data-testid="forgot-submit-btn" disabled={loading}
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 rounded-full transition-colors disabled:opacity-60">
                  {loading ? "Изпращане..." : "Изпрати линк за смяна"}
                </button>
                <button type="button" data-testid="forgot-back-btn" onClick={() => setMode("login")}
                  className="w-full text-sm text-slate-400 hover:text-slate-600 transition-colors">← Обратно към входа</button>
              </form>
            )
          ) : (
            <>
              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                  <input
                    data-testid="name-input" required placeholder="Име" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none transition-shadow"
                  />
                )}
                <input
                  data-testid="email-input" required type="email" placeholder="Имейл" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none transition-shadow"
                />
                <input
                  data-testid="password-input" required type="password" placeholder="Парола" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-sky-300 outline-none transition-shadow"
                />
                {mode === "login" && (
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer select-none">
                      <input data-testid="remember-checkbox" type="checkbox" checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-sky-500 focus:ring-sky-300 accent-sky-500" />
                      Остави ме логнат
                    </label>
                    <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setSent(false); }}
                      className="text-sm font-bold text-sky-600 hover:underline">Забравена парола?</button>
                  </div>
                )}
                <button
                  data-testid="submit-auth-btn" disabled={loading}
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold py-3 rounded-full transition-colors disabled:opacity-60"
                >
                  {loading ? "Моля изчакайте..." : mode === "register" ? "Създай профил" : "Влез"}
                </button>
              </form>

              <div className="flex items-center gap-3 my-5">
                <div className="h-px bg-slate-200 flex-1" />
                <span className="text-xs text-slate-400 font-semibold">или</span>
                <div className="h-px bg-slate-200 flex-1" />
              </div>
              <button
                data-testid="google-login-btn"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + "/app";
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full flex items-center justify-center gap-3 border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 font-bold text-slate-700 py-3 rounded-full transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
                Продължи с Google
              </button>
              <div className="mt-6 space-y-1.5">
                {["Безплатен старт с 5 AI заявки", "Без кредитна карта", "Готово за 30 секунди"].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-xs text-slate-400"><Check size={14} className="text-emerald-500" /> {t}</div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
